import { getQueryClient } from 'app/get-query-client';
import { startSessionTeardownEffects } from 'lib/stores/sessionTeardownEffects';
import { applyForcedDisconnectTeardown, loadUserSession, resetUserSession } from 'lib/stores/userSessionStore';
import { disposeDatadog } from 'lib/telemetry/datadogInit';
import { bankAccountsKeys } from 'queries/bankAccounts';
import { bankTransfersKeys } from 'queries/bankTransfers';
import { creditHistoryDerivedKeys } from 'queries/creditHistory';
import { refundKeys } from 'queries/refunds';
import { ssnStatusKeys } from 'queries/ssn';
import { stopTradingSync } from 'stores/tradingSync';
import { useBankFlowStore } from 'stores/zustand/bankFlowStore';
import { useBannerStore } from 'stores/zustand/bannerStore';
import { attachColorPaletteBridge } from 'stores/zustand/colorPaletteBridge';
import { useCryptoFlowStore } from 'stores/zustand/cryptoFlowStore';
import { usePaymentsFlowStore } from 'stores/zustand/paymentsFlowStore';
import { useReferralStore } from 'stores/zustand/referralStore';
import { useSocialOnboardingStore } from 'stores/zustand/socialOnboardingStore';
import { disposeLegacyExchangeWsClient, legacyExchangeWsClient } from 'transport/wsClients';

import { registerAppLogoutTeardown } from './appLogoutTeardown';

/**
 * Logout teardown for the app's global feature state. Runs on every session end
 * — explicit logout (`userSessionStore.endSession`) and forced disconnect
 * (`applyForcedDisconnectTeardown`) — as well as once more inside `stop()` on
 * final unmount teardown.
 *
 * Deliberately stateless: it only touches module-level singletons (the zustand
 * flow stores, the query cache, the legacy socket), so it is safe to call
 * without a live `startAppEffects` handle and needs no per-instance closure.
 */
export function resetAppStateOnLogout(): void {
    resetUserSession();
    useBankFlowStore.getState().actions.dispose();
    useCryptoFlowStore.getState().actions.dispose();
    usePaymentsFlowStore.getState().actions.dispose();
    useBannerStore.getState().actions.dispose();
    useSocialOnboardingStore.getState().actions.dispose();
    // Runs on every logout — without this, logging back into the same account
    // within the default 120s staleTime window would render stale bank account/
    // transfer data from the ended session.
    getQueryClient().removeQueries({ queryKey: bankAccountsKeys.all });
    getQueryClient().removeQueries({ queryKey: bankTransfersKeys.all });
    // These three are cached with staleTime: Infinity (never refetch once
    // fresh), so unlike most migrated user queries they won't self-correct on
    // re-login without an explicit clear.
    getQueryClient().removeQueries({ queryKey: creditHistoryDerivedKeys.all });
    getQueryClient().removeQueries({ queryKey: refundKeys.all });
    getQueryClient().removeQueries({ queryKey: ssnStatusKeys.all });
    // Runs on every logout (endSession / forced disconnect) and on unmount
    // teardown via stop(). Detaches the legacy stores' subscriptions from their
    // socket (they re-attach on the next login connect()). The socket is rotated
    // because its handshake can carry authentication; public event-contract
    // streams immediately resume on the replacement socket.
    legacyExchangeWsClient.disconnect();
}

/**
 * Starts the app's global client-side effects and returns a `stop()` that fully
 * tears them down. Owns the color palette bridge, auth-driven session teardown,
 * the referral cookie bootstrap, and the initial session restore.
 *
 * Client-only: invoked from `AppEffectsBootstrap`'s mount effect, so it never
 * runs during SSR/prerender (the color palette bridge's stateful subscriptions
 * are browser-only anyway, and first paint is handled by the pre-hydration
 * script in `app/layout.tsx`).
 */
export function startAppEffects(): () => void {
    registerAppLogoutTeardown(resetAppStateOnLogout);

    const disposeColorPaletteBridge = attachColorPaletteBridge();
    const stopSessionTeardownEffects = startSessionTeardownEffects(() => applyForcedDisconnectTeardown());

    useReferralStore.getState().actions.initFromCookie();
    // Bootstrap a session restored from cookies/localStorage on browser reload —
    // fetches the profile, starts/connects the FE session socket, subscribes
    // user_orders, and seeds the SSN/user-properties caches. Safe to call
    // unconditionally: it no-ops when there's no userId, and the fresh-login path
    // (handleLoginSuccess -> loadUserSession) runs long after mount, so this
    // doesn't double-fire on login.
    void loadUserSession();

    return function stop(): void {
        // startTradingSync()/stopTradingSync() are reference-counted against
        // useTradingSync's single mount effect. resetAppStateOnLogout() also runs
        // on every logout while the bootstrap stays mounted, so stopping trading
        // sync there would permanently kill it until a remount. Only stop it here.
        stopTradingSync();
        stopSessionTeardownEffects();
        resetAppStateOnLogout();
        // The color palette bridge's subscriptions read live state at call time,
        // so they stay correct across a logout -> login cycle within the same
        // mount — unlike resetAppStateOnLogout(), this must not run on every
        // logout. Only tear it down here, on unmount.
        disposeColorPaletteBridge();
        // Forget the legacy client's subscription registrations entirely so a
        // future mount starts clean (resetAppStateOnLogout only detached them,
        // keeping registrations for post-logout re-login).
        disposeLegacyExchangeWsClient();
        disposeDatadog();
    };
}
